import ConnectionLayer from './ConnectionLayer';
import Editor from './Editor';
import Layer from './Layer';
import NodeLayer, { questionTypes } from './NodeLayer';
import Rect from './Rect';
import Vector2 from './Vector2';

const handleRadius = 5 * window.devicePixelRatio;

function drawRoundedRect(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
) {
  if (width < 2 * radius) radius = width / 2;
  if (height < 2 * radius) radius = height / 2;
  context.beginPath();
  context.moveTo(x + radius, y);
  context.arcTo(x + width, y, x + width, y + height, radius);
  context.arcTo(x + width, y + height, x, y + height, radius);
  context.arcTo(x, y + height, x, y, radius);
  context.arcTo(x, y, x + width, y, radius);
  context.closePath();
}

function fillMultiLineText(
  context: CanvasRenderingContext2D,
  text: string,
  position: Vector2,
  padding: Vector2,
  width: number,
  fontSize: number,
  lineHeight: number
): Rect {
  const globalWords = text.trim().split('\n');
  let newText = '';
  var maxWidth = 0;
  var minWidth = 0;
  var y = position.y + fontSize + padding.y;
  var x = position.x + padding.x;

  for (let i = 0; i < globalWords.length; i++) {
    var words = globalWords[i].split(' ');
    var line = '';
    maxWidth = Math.max(0, width - padding.x * 2);
    minWidth = padding.x;

    context.font = `${fontSize}px Roboto`;
    for (var n = 0; n < words.length; n++) {
      minWidth = Math.max(context.measureText(words[n]).width, minWidth);
    }
    for (var n = 0; n < words.length; n++) {
      var testLine = line + words[n] + ' ';
      var metrics = context.measureText(testLine);
      var testWidth = metrics.width;
      if (testWidth > maxWidth && n > 0) {
        context.fillText(line, x, y);
        line = words[n] + ' ';
        y += lineHeight;
      } else {
        line = testLine;
      }
    }
    context.fillText(line, x, y);
    y += lineHeight;
  }

  return new Rect(
    position,
    new Vector2(
      minWidth + padding.x * 2,
      y + 0.5 * fontSize + padding.y - position.y
    )
  );
}

function fillSingleLineText(
  context: CanvasRenderingContext2D,
  text: string,
  position: Vector2,
  padding: Vector2,
  width: number,
  fontSize: number,
  lineHeight: number
) {
  var words = text.split(/\s+/g);
  var line = '';
  var y = position.y + fontSize + padding.y;
  var x = position.x + padding.x;
  var maxWidth = Math.max(0, width - padding.x * 2);

  context.font = `${fontSize}px Roboto`;

  for (var n = 0; n < words.length; n++) {
    var testLine = line + words[n] + ' ';
    var metrics = context.measureText(testLine);
    var testWidth = metrics.width;
    if (testWidth > maxWidth && n > 0) {
      context.fillText(line, x, y);
      line = words[n] + ' ';
      y += lineHeight;
      return;
    } else {
      line = testLine;
    }
  }

  context.fillText(line, x, y);
}

function drawArrowhead(
  context: CanvasRenderingContext2D,
  from: Vector2,
  to: Vector2
) {
  var x_center = to.x;
  var y_center = to.y;

  var angle: number;
  var x: number;
  var y: number;

  var radius = handleRadius * 2;

  context.beginPath();

  angle = Math.atan2(to.y - from.y, to.x - from.x);
  x = radius * Math.cos(angle) + x_center;
  y = radius * Math.sin(angle) + y_center;

  context.moveTo(x, y);

  angle += (1.0 / 3.0) * (2 * Math.PI);
  x = radius * Math.cos(angle) + x_center;
  y = radius * Math.sin(angle) + y_center;

  context.lineTo(x, y);

  angle += (1.0 / 3.0) * (2 * Math.PI);
  x = radius * Math.cos(angle) + x_center;
  y = radius * Math.sin(angle) + y_center;

  context.lineTo(x, y);
  context.closePath();
  context.fill();
}

export function renderConnectionLayer(
  layer: ConnectionLayer,
  context: CanvasRenderingContext2D
) {
  var startPoint = layer.scene.camera.transformPointWorldToScreen(
    layer.startPoint
  );
  var endPoint = layer.scene.camera.transformPointWorldToScreen(layer.endPoint);

  context.beginPath();
  context.moveTo(startPoint.x, startPoint.y);
  context.lineTo(endPoint.x, endPoint.y);

  context.strokeStyle = 'rgb(155, 66, 227)';
  context.fillStyle = 'rgb(155, 66, 227)';
  context.lineWidth = 2 * window.devicePixelRatio;
  context.stroke();

  var endIntersectionPoint = layer.getEndPointIntersection();
  endIntersectionPoint =
    layer.scene.camera.transformPointWorldToScreen(endIntersectionPoint);

  drawArrowhead(context, startPoint, endIntersectionPoint);

  var startIntersectionPoint = layer.getStartPointIntersection();
  startIntersectionPoint = layer.scene.camera.transformPointWorldToScreen(
    startIntersectionPoint
  );

  var midpoint = endIntersectionPoint.add(startIntersectionPoint);
  midpoint = midpoint.mulScalar(0.5);

  layer.setMidPoint(midpoint);

  context.beginPath();
  context.arc(midpoint.x, midpoint.y, handleRadius * 2, 0, 2 * Math.PI);

  context.fillStyle = 'white';
  context.fill();
  context.stroke();

  let editor = layer.scene as Editor;

  var isSelected = editor.selectedLayer == layer;

  if (isSelected) {
    context.fillStyle = 'rgb(155, 66, 227)';
    context.beginPath();
    context.arc(midpoint.x, midpoint.y, handleRadius * 1.2, 0, 2 * Math.PI);
    context.fill();
  }
}

function drawText(layer: NodeLayer, context: CanvasRenderingContext2D) {
  var rect = layer.scene.camera.transformRectWorldToScreen(layer.boundingRect);
  var fontSize = Math.floor(32 * layer.scene.camera.scale);
  var lineHeight = 36 * layer.scene.camera.scale;
  var padding = 36 * layer.scene.camera.scale;

  context.fillStyle = layer.fgColor;

  var minRect = fillMultiLineText(
    context,
    layer.questionText,
    new Vector2(rect.position.x, rect.position.y),
    new Vector2(padding, padding),
    rect.size.x,
    fontSize,
    lineHeight
  );

  layer.minSize = minRect.size.divScalar(layer.scene.camera.scale);
}

function drawStartNode(layer: NodeLayer, context: CanvasRenderingContext2D) {
  if (layer.scene.startLayer != layer) {
    return;
  }

  var rect = layer.scene.camera.transformRectWorldToScreen(layer.boundingRect);
  var fontSize = Math.floor(52 * layer.scene.camera.scale);
  context.font = `${fontSize}px Material Icons`;

  context.fillStyle = layer.fgColor;
  context.globalAlpha = 0.6;
  var endPoint = rect.position.add(new Vector2(rect.size.x, 0));

  var offset = fontSize * 1.4;

  context.fillText('storage', endPoint.x - offset, endPoint.y + offset);

  context.globalAlpha = 1;
}

function drawExpandArrow(layer: NodeLayer, context: CanvasRenderingContext2D) {
  if (layer.questionType == questionTypes.text) {
    return;
  }

  var rect = layer.scene.camera.transformRectWorldToScreen(layer.boundingRect);
  var fontSize = Math.floor(42 * layer.scene.camera.scale);
  context.font = `${fontSize}px Material Icons`;

  context.fillStyle = layer.fgColor;
  context.globalAlpha = 0.6;
  var endPoint = rect.position.add(rect.size);

  var offset = fontSize * 1.2;

  var icon = layer.arrowOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down';

  context.fillText(icon, endPoint.x - offset, endPoint.y - 0.1 * offset);

  context.globalAlpha = 1;
}

function drawExpandArea(layer: NodeLayer, context: CanvasRenderingContext2D) {
  if (layer.arrowOpen == false) {
    return;
  }

  var choices = layer.choices.filter((it) => it != '');

  for (var i = 0; i < choices.length; i++) {
    var text = choices[i];

    var fontSize = Math.floor(22 * layer.scene.camera.scale);
    var lineHeight = 22 * layer.scene.camera.scale;
    var padding = 14 * layer.scene.camera.scale;

    var rect = layer.scene.camera.transformRectWorldToScreen(
      layer.boundingRect.add(new Vector2(0, layer.boundingRect.size.y))
    );

    rect = rect.add(new Vector2(0, i * (fontSize + 2 * padding)));

    context.fillStyle = layer.fgColor;

    context.font = `${fontSize}px Material Icons`;
    context.fillText(
      'list',
      rect.position.x + padding,
      rect.position.y + padding * 1.25 + fontSize
    );

    fillSingleLineText(
      context,
      text,
      rect.position.add(new Vector2(fontSize * 1.3, 0)),
      new Vector2(padding, padding),
      rect.size.x,
      fontSize,
      lineHeight
    );
  }
}

export function renderNodeLayer(
  layer: NodeLayer,
  context: CanvasRenderingContext2D
) {
  var originalRect = layer.scene.camera.transformRectWorldToScreen(
    layer.boundingRect
  );
  var rect = originalRect;
  var choices = layer.choices.filter((it) => it != '');

  if (layer.arrowOpen && choices.length != 0) {
    var lineHeight = 22 * layer.scene.camera.scale;
    var padding = 14 * layer.scene.camera.scale;
    var extra =
      layer.choices.length * (lineHeight + 2 * padding) + padding * 0.5;

    rect = new Rect(rect.position, rect.size.add(new Vector2(0, extra)));
  }

  drawRoundedRect(
    context,
    rect.position.x,
    rect.position.y,
    rect.size.x,
    rect.size.y,
    4 * window.devicePixelRatio
  );

  context.shadowColor = 'rgba(0, 0, 0, 0.3)';
  context.shadowBlur = 8 * window.devicePixelRatio;

  context.fillStyle = layer.bgColor;
  context.fill();

  context.shadowBlur = 0;

  if (layer.arrowOpen) {
    context.fillStyle = layer.fgColor;
    context.globalAlpha = 0.4;
    context.fillRect(
      originalRect.position.x,
      originalRect.position.y + originalRect.size.y,
      originalRect.size.x,
      2 * layer.scene.camera.scale
    );

    context.globalAlpha = 1;
  }

  drawStartNode(layer, context);
  drawExpandArrow(layer, context);
  drawText(layer, context);
  drawExpandArea(layer, context);
}

export function renderLayer(layer: Layer, context: CanvasRenderingContext2D) {
  if (layer instanceof NodeLayer) {
    renderNodeLayer(layer, context);
  }

  if (layer instanceof ConnectionLayer) {
    renderConnectionLayer(layer, context);
  }
}
